import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Stethoscope } from 'lucide-react';
import { Panel, fmtDate } from './design';

// Workers' notes that may show a change in needs (a fall, confusion, not eating, a hospital visit,
// a wound...), or that someone flagged. A care partner reviews each: no change, care plan updated,
// or a reassessment asked for.
const OUTCOMES = [['no_change', 'No change in needs'], ['care_plan_updated', 'Care plan updated'], ['reassessment', 'Reassessment requested']];

export default function NeedsReviews() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);   // { noteId, outcome, note }
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/needs-reviews')
      .then((r) => setRows(r.data || []))
      .catch((e) => { setRows([]); setError(e.response?.data?.message || 'Could not load the notes to review for a change in needs.'); });
  }, []);
  useEffect(() => { load(); }, [load]);
  // A failed load must not look the same as "nothing to review": a note about a fall would then go
  // unseen. So it says so, with a way to try again.
  if (error) {
    return (
      <p className="mb-5 flex flex-wrap items-center gap-2 text-[13px] text-red-800" role="alert">
        <Stethoscope size={14} aria-hidden="true" />
        {error}
        <button type="button" className="btn-ghost btn-sm" onClick={load}>Try again</button>
      </p>
    );
  }
  if (!rows.length) return null;
  const save = async () => {
    try { await api.post(`/aged-care/needs-reviews/${open.noteId}`, { outcome: open.outcome, note: open.note }); toast.success('Reviewed'); setOpen(null); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not record the review'); }
  };
  return (
    <Panel title={`Notes to review for a change in needs (${rows.length})`} icon={Stethoscope} className="mb-5" bodyClass="divide-y divide-slate-100">
      {rows.map((r) => (
        <div key={r.noteId} className="px-4 py-2.5 text-[13px]">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-slate-900 font-medium">{r.participant} <span className="font-normal text-slate-500">· {fmtDate(r.date)}{r.worker ? ` · ${r.worker}` : ''}{r.flagged ? ' · flagged' : r.word ? ` · mentions "${r.word}"` : ''}</span></span>
            {!(open && open.noteId === r.noteId) && <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen({ noteId: r.noteId, outcome: 'no_change', note: '' })}>Review</button>}
          </div>
          {r.excerpt && <p className="text-slate-600 mt-0.5">"…{r.excerpt}…"</p>}
          {open && open.noteId === r.noteId && (
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <select aria-label="Outcome" className="input-field !w-auto !py-1" value={open.outcome} onChange={(e) => setOpen((o) => ({ ...o, outcome: e.target.value }))}>
                {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input aria-label="Note" className="input-field !w-72 !py-1" placeholder="What was done" value={open.note} onChange={(e) => setOpen((o) => ({ ...o, note: e.target.value }))} />
              <button type="button" className="btn-primary btn-sm" onClick={save}>Save</button>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(null)}>Cancel</button>
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
}
